import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Sparkles, Wand2, Map, Star, ArrowRight, Gamepad2, Globe,
  NotebookPen, Hammer, Check, Package, GitBranch, Rocket,
  BookOpen, Users, GraduationCap, MessageSquarePlus, Loader2,
  Video, X, Circle, Dna, Crown, Feather, Compass, Heart, PenLine,
} from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VoiceButton from "../components/VoiceButton";
import Reveal from "../components/Reveal";
import RoyalDivider from "../components/RoyalDivider";

const BRAND = "https://customer-assets.emergentagent.com/job_1a752c50-99ae-44d4-a5d5-08c385e2832e/artifacts/enaxu6ve_Product%402x.png";

// —————————————————————————————————————————————————————————
//   Durga Programming Adventures · Royal Ghibli Storybook edition
//   Narrative flow: Crown → Proof → Three Pillars → Six Apps →
//   Process → Journey teaser → Ms. Pradhan → Books → Stories →
//   Pricing → Call to Adventure.
// —————————————————————————————————————————————————————————

export default function Landing() {
  const { t } = useTranslation();
  const heroPortraitRef = useRef(null);

  // Subtle parallax on the hero portrait — respects reduced motion via CSS.
  useEffect(() => {
    const el = heroPortraitRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        // Only when the portrait is near the viewport
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const centerOffset = rect.top + rect.height / 2 - window.innerHeight / 2;
        const py = Math.max(-24, Math.min(24, -centerOffset / 30));
        el.style.setProperty("--py", `${py}px`);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <div className="bg-cream" data-testid="landing-page">

      {/* ─── 1. HERO — the book unfolds ─── */}
      <section className="relative pt-16 md:pt-24 pb-20 overflow-hidden" data-testid="hero-section">
        {/* Ornamental gold arcs, hand-drawn feel */}
        <svg aria-hidden="true" className="absolute -top-24 -left-24 w-[380px] h-[380px] opacity-40 pointer-events-none" viewBox="0 0 400 400">
          <defs>
            <linearGradient id="goldArc" x1="0" x2="1">
              <stop offset="0%" stopColor="#F4A261" stopOpacity="0" />
              <stop offset="60%" stopColor="#F4A261" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#E76F51" stopOpacity="0" />
            </linearGradient>
          </defs>
          <circle cx="200" cy="200" r="170" fill="none" stroke="url(#goldArc)" strokeWidth="1.5" strokeDasharray="4 6" />
          <circle cx="200" cy="200" r="140" fill="none" stroke="url(#goldArc)" strokeWidth="1" strokeDasharray="2 8" />
        </svg>
        <svg aria-hidden="true" className="absolute -bottom-24 -right-24 w-[420px] h-[420px] opacity-30 pointer-events-none" viewBox="0 0 400 400">
          <path d="M0,300 Q100,240 200,300 T400,300" fill="none" stroke="#E76F51" strokeWidth="1.2" opacity="0.55" />
          <path d="M0,340 Q100,280 200,340 T400,340" fill="none" stroke="#F4A261" strokeWidth="1" opacity="0.5" />
        </svg>

        <div className="container-narrow grid lg:grid-cols-12 gap-12 items-center relative">
          <Reveal className="lg:col-span-7 relative">
            <div className="inline-flex items-center gap-2 mb-4 text-[11px] uppercase tracking-[0.32em] font-bold text-marigold">
              <BookOpen className="w-3.5 h-3.5" /> Programming, told as a book
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-[5.25rem] font-semibold tracking-tight leading-[1.02] mb-6 text-ink">
              A book that unfolds
              <br />
              into <span className="italic text-marigold gold-underline">twelve worlds</span>,
              <br />
              a <span className="italic">constellation</span>,
              <br />
              and a <span className="italic">road trip</span>.
            </h1>
            <p className="text-lg md:text-xl text-ink2 max-w-xl leading-relaxed mb-8">
              Durga Programming Adventures is an enormous pop-up book of computer science —
              2,500 chapters woven into twelve worlds, threaded by adventure narratives,
              and taught side-by-side with real apps and games you actually build.
              Ms. Pradhan reads with you.
            </p>
            <div className="flex flex-wrap gap-3 relative" data-testid="hero-cta-row">
              <Link to="/learn" className="btn-primary text-base px-6 py-3 hover-lift" data-testid="hero-start-adventure">
                <BookOpen className="w-4 h-4" /> Open the book <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/adventures/lost-internet" className="btn-outline-ink text-base px-5 py-3 hover-lift" data-testid="hero-walk-journey">
                <Compass className="w-4 h-4" /> Read The Lost Internet
              </Link>
              <span className="hidden md:inline-block font-hand text-2xl text-marigold -rotate-6 absolute -right-2 -top-4 pointer-events-none select-none" aria-hidden="true">
                free to start
              </span>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-ink2">
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal" /> 2,560 chapters, four ways to read each</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal" /> A living concept graph</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-teal" /> A teacher who rides shotgun</span>
            </div>
          </Reveal>

          <Reveal delay={2} className="lg:col-span-5 relative">
            <div ref={heroPortraitRef} className="parallax-hero">
              <div className="relative rounded-3xl overflow-hidden border-4 border-ink shadow-[16px_16px_0_0_#1F1A17] hover-lift">
                <img src={BRAND} alt="Durga — our guide" className="w-full h-auto" />
                {/* Marigold corner ornaments */}
                <div className="absolute top-3 left-3 text-marigold" aria-hidden="true">
                  <svg width="42" height="42" viewBox="0 0 42 42"><path d="M2 2 L14 2 L14 4 L4 4 L4 14 L2 14 Z M28 2 L40 2 L40 14 L38 14 L38 4 L28 4 Z" fill="currentColor" opacity="0.9"/></svg>
                </div>
                <div className="absolute bottom-3 right-3 text-marigold rotate-180" aria-hidden="true">
                  <svg width="42" height="42" viewBox="0 0 42 42"><path d="M2 2 L14 2 L14 4 L4 4 L4 14 L2 14 Z M28 2 L40 2 L40 14 L38 14 L38 4 L28 4 Z" fill="currentColor" opacity="0.9"/></svg>
                </div>
              </div>
              <div className="absolute -bottom-5 -left-5 bg-cream border-2 border-ink rounded-xl px-3 py-2 shadow-[5px_5px_0_0_#1F1A17]">
                <div className="text-[10px] text-ink2 uppercase tracking-widest font-bold">Guided by</div>
                <div className="font-heading font-semibold text-sm text-ink">Durga & Ms. Durga Pradhan</div>
              </div>
              <div className="hidden md:flex absolute -top-4 -right-4 bg-marigold text-ink border-2 border-ink rounded-full w-16 h-16 items-center justify-center shadow-[4px_4px_0_0_#1F1A17]">
                <div className="text-center">
                  <div className="font-hand text-xs leading-none">est.</div>
                  <div className="font-heading text-lg leading-none">2026</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── 1b. WHAT THIS IS — four ways in ─── */}
      <Reveal>
        <section className="container-narrow pt-4 pb-16" data-testid="four-ways-section">
          <div className="text-center mb-10">
            <div className="text-[11px] uppercase tracking-[0.32em] font-bold text-marigold mb-2">Four ways in</div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-ink leading-tight max-w-3xl mx-auto">
              However you like to learn — the book meets you there.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <WayCard
              testid="way-worlds"
              to="/learn"
              kicker="The Twelve Worlds"
              title="Read it as a book"
              body="2,500 chapters classified into twelve worlds of computing, from Foundations to Programming Biology."
              accent="text-marigold"
              badge="I"
            />
            <WayCard
              testid="way-graph"
              to="/learn/graph"
              kicker="The Constellation"
              title="See it as a graph"
              body="A hand-drawn concept map — recursion → trees → compilers in one glance. Tap a world to zoom in."
              accent="text-teal"
              badge="II"
            />
            <WayCard
              testid="way-adventures"
              to="/adventures"
              kicker="Adventures"
              title="Follow a story"
              body="Narrative tours that thread real chapters through one plot. Start with The Lost Internet."
              accent="text-marigold"
              badge="III"
            />
            <WayCard
              testid="way-library"
              to="/library"
              kicker="The Library"
              title="Read the whole book"
              body="2,560 chapters imported from the field — searchable, tag-filtered, and free for everyone."
              accent="text-teal"
              badge="IV"
            />
          </div>
        </section>
      </Reveal>

      {/* ─── 1c. Depth demo removed — condensed into Four Ways ─── */}

      {/* ─── 2. PROOF STRIP — a ribbon between sections ─── */}
      <Reveal>
        <section className="border-y-2 border-royal/25 bg-parchment/60 py-6" data-testid="proof-strip">
          <div className="container-narrow grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <ProofStat n="6" label="apps in the atelier" />
            <ProofStat n="2,560" label="library chapters" />
            <ProofStat n="12" label="worlds of computing" />
            <ProofStat n="Free" label="for every learner" />
          </div>
        </section>
      </Reveal>

      {/* ─── 3. THREE PILLARS — Build · Learn · Play ─── */}
      <section className="container-narrow py-20" data-testid="pillars-section">
        <Reveal className="mb-10 text-center">
          <div className="royal-eyebrow justify-center mb-3"><Feather className="w-3.5 h-3.5" /> Chapter I · What awaits</div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold max-w-2xl mx-auto text-royal">
            Three doors, one <span className="italic gold-underline">royal road</span>.
          </h2>
          <p className="text-ink2 mt-4 max-w-xl mx-auto">
            Pick a door — build, learn, or play. Every path is lit by the same lantern.
          </p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5">
          <Reveal delay={1}>
            <PillarCard
              tint="bg-saffron/12 border-royal/60"
              icon={<Wand2 className="w-5 h-5" />}
              iconTint="bg-saffron/30 text-royal"
              eyebrow="Build"
              title="Prompt · code · ship."
              body="Six ateliers, from Apple apps to Linux releases. Real Xcode, real .deb, real websites."
              to="/apps"
              cta="Enter the atelier"
              testid="pillar-build"
            />
          </Reveal>
          <Reveal delay={2}>
            <PillarCard
              tint="bg-teal/10 border-royal/60"
              icon={<Compass className="w-5 h-5" />}
              iconTint="bg-teal/25 text-royal"
              eyebrow="Learn"
              title="Twelve worlds of computing."
              body="2,560 chapters, a knowledge graph, and story-shaped adventures — free for everyone."
              to="/learn"
              cta="Enter the atlas"
              testid="pillar-learn"
            />
          </Reveal>
          <Reveal delay={3}>
            <PillarCard
              tint="bg-gold/15 border-royal/60"
              icon={<Heart className="w-5 h-5" />}
              iconTint="bg-gold/30 text-royal"
              eyebrow="Play"
              title="Books, games, biology."
              body="Pop-up books, SpriteKit games, and a biology workbench. Curiosity is the tuition."
              to="/books"
              cta="Peek inside"
              testid="pillar-play"
            />
          </Reveal>
        </div>
      </section>

      <RoyalDivider label="Chapter II · The atelier" />

      {/* ─── 4. PRODUCT — 6 Apps Bento ─── */}
      <section className="container-narrow py-20" data-testid="apps-bento-section">
        <Reveal className="mb-10">
          <div className="royal-eyebrow mb-3"><Sparkles className="w-3.5 h-3.5" /> Product · What you get</div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold max-w-2xl text-royal">
            Six apps that treat you like a <span className="italic gold-underline">serious maker</span>.
          </h2>
          <div className="mt-2 text-[11px] uppercase tracking-widest font-bold text-[#7B5EA7]">Now with Mendel · biological programming workbench</div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <Reveal className="md:col-span-7 md:row-span-2" delay={1}>
            <AppCard
              className="min-h-[380px]"
              tint="bg-marigold/10 border-royal/70"
              icon={<Globe className="w-5 h-5" />}
              iconTint="bg-marigold/20 text-marigold"
              eyebrow="Product · Web"
              title="Website Studio"
              desc="Describe a site. Get a Next.js + Tailwind project with real copy, palette, and a live preview. One tap ships it to Vercel."
              outcome="What you leave with: a working custom domain + code you own."
              to="/website-studio"
              testid="app-card-website"
            >
              <div className="mt-4 bg-codebg rounded-xl p-4 border-2 border-royal font-mono text-xs text-cream/90 shadow-[4px_4px_0_0_#2E1A47]">
                <div className="text-teal">▸ prompt</div>
                <div className="text-cream/80 italic mb-2">&ldquo;a bakery site with an about page and a menu&rdquo;</div>
                <div className="text-marigold">✓ generated 12 files</div>
                <div className="text-cream/60">→ deploying to bakery-durga.vercel.app</div>
                <div className="text-teal">✓ live</div>
              </div>
            </AppCard>
          </Reveal>

          <Reveal className="md:col-span-5" delay={2}>
            <AppCard
              tint="bg-saffron/15 border-royal/70"
              icon={<Wand2 className="w-5 h-5" />}
              iconTint="bg-saffron/25 text-royal"
              eyebrow="Product · Apple"
              title="Prompt Studio"
              desc="Describe an Apple app. Get screens, schema, SwiftUI code, and a real Xcode starter."
              outcome="Ships iOS · iPadOS · macOS · tvOS · visionOS."
              to="/studio"
              testid="app-card-studio"
            />
          </Reveal>

          <Reveal className="md:col-span-5" delay={3}>
            <AppCard
              tint="bg-teal/10 border-royal/70"
              icon={<Gamepad2 className="w-5 h-5" />}
              iconTint="bg-teal/20 text-teal"
              eyebrow="Product · Games"
              title="Game Studio"
              desc="SpriteKit, SceneKit, or RealityKit — visual scene editor + AI code with Game Center + TestFlight wired."
              outcome="Multiplayer & leaderboards, day one."
              to="/games"
              testid="app-card-games"
            />
          </Reveal>

          <Reveal className="md:col-span-6" delay={1}>
            <AppCard
              tint="bg-ink/[0.06] border-royal/70"
              icon={<NotebookPen className="w-5 h-5" />}
              iconTint="bg-ink/10 text-ink"
              eyebrow="Product · Documentation"
              title="Knuth"
              desc="Point it at a GitHub repo or ZIP. Get README, architecture, Mermaid UML, and traceable pseudocode."
              outcome="Every doc references the exact line it explains."
              to="/apps/knuth"
              badge="Beta"
              testid="app-card-knuth"
            >
              <div className="mt-4 flex items-center gap-2 text-xs text-ink2 font-mono">
                <GitBranch className="w-3.5 h-3.5" /> repo → <span className="text-teal">README + arch + UML</span>
              </div>
            </AppCard>
          </Reveal>

          <Reveal className="md:col-span-6" delay={2}>
            <AppCard
              style={{ background: "#8DA3B015", borderColor: "#2E1A47B3" }}
              tint="border-2"
              icon={<Hammer className="w-5 h-5" />}
              iconTint="text-[#526972]"
              iconStyle={{ background: "#8DA3B025" }}
              eyebrow="Product · Releases"
              title="Kailash"
              desc="Ubuntu · Debian · Fedora · Arch · openSUSE and more. Import → pick target → AI writes the build config, then Kailash produces real .deb / .rpm / PKGBUILD files."
              outcome="Cross-compile to many distros in a single click."
              to="/apps/kailash"
              badge="Beta"
              testid="app-card-kailash"
            >
              <div className="mt-4 flex flex-wrap gap-1.5 text-[10px] font-mono text-ink2">
                {["deb", "rpm", "PKGBUILD", "AppImage", "docker", "tar.gz"].map((k) => (
                  <span key={k} className="px-2 py-0.5 rounded-full bg-cream border border-royal/30">{k}</span>
                ))}
              </div>
            </AppCard>
          </Reveal>

          <Reveal className="md:col-span-12" delay={1}>
            <AppCard
              style={{ background: "#7B5EA715", borderColor: "#2E1A47B3" }}
              tint="border-2"
              icon={<Dna className="w-5 h-5" />}
              iconTint="text-[#7B5EA7]"
              iconStyle={{ background: "#7B5EA725" }}
              eyebrow="Product · Biology"
              title="Mendel"
              desc="Explore human chromosomes. Read curated genes. Simulate Mendelian inheritance. Ask an OpenAI-powered research assistant. 20 lessons by Ms. Pradhan from base pairs to first project."
              outcome="Program the language of life — safely, and with your workspace as context."
              to="/apps/mendel"
              badge="New"
              testid="app-card-mendel"
            >
              <div className="mt-4 flex flex-wrap gap-1.5 text-[10px] font-mono text-ink2">
                {["24 chromosomes", "HBB · BRCA1 · TP53", "Punnett simulator", "Mendel AI", "FASTA · VCF · CSV"].map((k) => (
                  <span key={k} className="px-2 py-0.5 rounded-full bg-cream border border-royal/30">{k}</span>
                ))}
              </div>
            </AppCard>
          </Reveal>
        </div>
      </section>

      {/* ─── 5. Process section removed — condensed into Apps Bento ─── */}

      {/* ─── 6. Journey section removed — content now surfaced through Learn + Library ─── */}

      <RoyalDivider label="Chapter V · The court" tone="light" />

      {/* ─── 7. PEOPLE — Ms. Pradhan ─── */}
      <section className="container-narrow py-20" data-testid="people-section">
        <Reveal>
          <div className="rounded-3xl p-8 md:p-12 bg-gradient-to-br from-saffron/15 via-parchment to-marigold/10 border-2 border-royal shadow-[10px_10px_0_0_#2E1A47]">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-4">
                <div className="relative hover-lift">
                  <div className="rounded-2xl overflow-hidden border-4 border-royal shadow-[6px_6px_0_0_#2E1A47]">
                    <img src={BRAND} alt="Ms. Pradhan" className="w-full h-auto" />
                  </div>
                  <div className="absolute -bottom-3 -right-3 bg-cream border-2 border-royal rounded-xl px-3 py-1.5 shadow-[4px_4px_0_0_#2E1A47]">
                    <div className="font-hand text-xl leading-none text-marigold">Ms. Pradhan</div>
                    <div className="text-[10px] text-ink2 uppercase tracking-widest font-bold">Teacher-in-residence</div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-8">
                <div className="royal-eyebrow mb-3"><Users className="w-3.5 h-3.5" /> People · For whom</div>
                <h2 className="font-heading text-4xl md:text-5xl font-semibold leading-tight mb-4 text-royal">
                  A teacher for the whole <span className="italic gold-underline">road trip</span>.
                </h2>
                <p className="text-ink leading-relaxed mb-4">
                  Ms. Pradhan taught computer science for two decades before she stepped into these pages.
                  She rides shotgun on your journey — explaining algorithms in Punjab, sketching state
                  machines in Himachal, and clapping when your first release ships in Tripura.
                </p>
                <div className="mb-5">
                  <VoiceButton
                    text="Namaste, adventurer. I'm Ms. Pradhan. For twenty years I taught computer science in schools across North India. Now I ride shotgun on your road trip — from prompting your first Apple app in Delhi to packaging a real Linux release in Tripura. Whenever you need a nudge, tap me. I'll be there."
                    label="Meet Ms. Pradhan"
                    data-testid="landing-pradhan-voice"
                  />
                </div>
                <div className="grid sm:grid-cols-4 gap-3 mb-6">
                  <AudienceTile label="Young makers"    sub="10–14 yrs" />
                  <AudienceTile label="Teachers"        sub="K–12 · uni" />
                  <AudienceTile label="Indie founders"  sub="MVP → App Store" />
                  <AudienceTile label="Curious grownups" sub="picks up code again" />
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link to="/learn" className="btn-royal text-sm hover-lift" data-testid="people-journey-cta">
                    <Map className="w-4 h-4" /> Enter the atlas <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/signup" className="btn-outline-ink text-sm hover-lift" data-testid="people-signup-cta">
                    Talk to Ms. Pradhan
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ─── 8. Physical evidence section removed — moved to /books page ─── */}

      {/* ─── 9. Testimonials wall removed until we have real stories ─── */}

      {/* ─── 10. PRICE — Tier snapshot ─── */}
      <section className="container-narrow py-20" data-testid="price-section">
        <Reveal className="text-center mb-10">
          <div className="royal-eyebrow justify-center mb-3"><PenLine className="w-3.5 h-3.5" /> Knowledge is free · Pay only for AI creation power</div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-royal">
            Learn free. <span className="italic gold-underline">Pay to build.</span>
          </h2>
          <p className="text-ink2 mt-4 max-w-xl mx-auto">
            Every lesson, world, adventure, and all 2,560 chapters — free for everyone, forever. Paid tiers unlock more AI prompts and creation quotas.
          </p>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { name: "Explorer",   price: "Free", tag: "All learning · always", desc: "Unlimited chapters + 5 AI prompts / mo", testid: "tier-explorer" },
            { name: "Solo",       price: "$12",  tag: "Best for tinkerers",    desc: "15 prompts · full Knuth + Kailash",   testid: "tier-solo", highlight: true },
            { name: "Apprentice", price: "$29",  tag: "Serious learners",      desc: "30 prompts · Swift → Canvas",         testid: "tier-apprentice" },
            { name: "Creator",    price: "$89",  tag: "Shipping weekly",       desc: "250 prompts · TestFlight · marketing", testid: "tier-creator" },
            { name: "Founder",    price: "$249", tag: "Full production",       desc: "1,500 prompts · team seats · priority", testid: "tier-founder" },
          ].map((tier, i) => (
            <Reveal delay={((i % 4) + 1)} key={tier.name}>
              <TierCard {...tier} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to="/pricing" className="btn-outline-ink text-sm inline-flex items-center gap-2 hover-lift" data-testid="price-see-full">
            See the full per-tier matrix <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/library" className="btn-royal text-sm inline-flex items-center gap-2 hover-lift" data-testid="price-start-reading">
            <BookOpen className="w-4 h-4" /> Start reading — it's free
          </Link>
        </Reveal>
      </section>

      {/* ─── 11. FOOTER CTA — Final call to adventure ─── */}
      <section className="bg-royal text-parchment py-24 relative overflow-hidden" data-testid="footer-cta">
        <svg aria-hidden="true" className="absolute -top-20 left-1/2 -translate-x-1/2 w-[700px] h-[300px] opacity-25 pointer-events-none" viewBox="0 0 700 300">
          <path d="M0,150 Q175,50 350,150 T700,150" fill="none" stroke="#C9A24A" strokeWidth="1.5" />
          <path d="M0,170 Q175,70 350,170 T700,170" fill="none" stroke="#E4C46B" strokeWidth="1" opacity="0.6" />
        </svg>
        <Reveal className="container-narrow text-center relative">
          <div className="text-gold2 text-xs uppercase tracking-[0.28em] font-bold mb-3 inline-flex items-center gap-1.5"><Crown className="w-3 h-3" /> {t("footer_cta.eyebrow", "Chapter VII · Your turn")}</div>
          <h2 className="font-heading text-5xl md:text-6xl font-semibold mb-4 leading-tight">
            {t("footer_cta.title", "Take the crown.")} <span className="italic gold-shimmer">{t("footer_cta.title_em", "Ride the road.")}</span>
          </h2>
          <p className="text-parchment/70 max-w-lg mx-auto mb-8">{t("footer_cta.body", "One free account. Six apps. Twelve stops. A teacher who cheers. Nothing to lose but the code you haven't written yet.")}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/signup" className="btn-gold text-base px-6 py-3 hover-lift" data-testid="footer-cta-signup">
              <Sparkles className="w-4 h-4" /> {t("footer_cta.cta_signup", "Begin the adventure")}
            </Link>
            <Link to="/learn" className="text-sm border-2 border-parchment/30 text-parchment px-5 py-3 rounded-full inline-flex items-center gap-2 hover:bg-parchment/10 transition-colors hover-lift" data-testid="footer-cta-map">
              <Map className="w-4 h-4" /> {t("footer_cta.cta_map", "Peek at the atlas")}
            </Link>
          </div>
        </Reveal>
      </section>

      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────

function WayCard({ testid, to, kicker, title, body, accent = "text-ink", badge }) {
  return (
    <Link
      to={to}
      className="group relative block rounded-3xl border-2 border-ink bg-cream shadow-[6px_6px_0_0_#1F1A17] hover:-translate-y-1 hover:shadow-[10px_10px_0_0_#1F1A17] transition-all p-6 overflow-hidden"
      data-testid={testid}
    >
      {badge && (
        <div className="absolute top-2 right-4 font-heading text-7xl font-semibold text-ink/[0.06] leading-none select-none pointer-events-none">
          {badge}
        </div>
      )}
      <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-2 relative">{kicker}</div>
      <h3 className={`font-heading text-xl md:text-2xl font-semibold ${accent} leading-tight mb-3 relative`}>{title}</h3>
      <p className="text-sm text-ink2 leading-relaxed mb-4 relative">{body}</p>
      <div className="inline-flex items-center gap-1 text-xs font-bold text-ink group-hover:text-marigold transition-colors relative">
        Enter <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}

function ProofStat({ n, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="font-heading text-3xl md:text-4xl font-semibold text-royal leading-none">{n}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink2 mt-1.5">{label}</div>
    </div>
  );
}

function PillarCard({ tint, icon, iconTint, eyebrow, title, body, to, cta, testid }) {
  return (
    <Link
      to={to}
      className={`block rounded-3xl border-2 p-6 h-full shadow-[6px_6px_0_0_#2E1A47] hover:-translate-y-1 hover:shadow-[9px_9px_0_0_#2E1A47] transition-all ${tint}`}
      data-testid={testid}
    >
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 ${iconTint}`}>
        {icon}
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-royal/80 mb-1">{eyebrow}</div>
      <h3 className="font-heading text-2xl font-semibold mb-2 leading-tight text-royal">{title}</h3>
      <p className="text-ink2 text-sm leading-relaxed mb-4">{body}</p>
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-royal">
        {cta} <ArrowRight className="w-4 h-4" />
      </span>
    </Link>
  );
}

function AppCard({ className = "", tint = "", style, icon, iconTint = "", iconStyle, eyebrow, title, desc, outcome, to, testid, badge, children }) {
  return (
    <Link
      to={to}
      className={`${className} rounded-3xl border-2 p-6 hover:-translate-y-1 transition-transform relative shadow-[6px_6px_0_0_#2E1A47] block h-full ${tint}`}
      style={style}
      data-testid={testid}
    >
      {badge && (
        <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest font-bold text-royal bg-gold border-2 border-royal rounded-full px-2 py-0.5">{badge}</span>
      )}
      <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 ${iconTint}`} style={iconStyle}>
        {icon}
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-royal/70 mb-1">{eyebrow}</div>
      <h3 className="font-heading text-2xl font-semibold mb-2 leading-tight text-royal">{title}</h3>
      <p className="text-ink2 text-sm leading-relaxed mb-3">{desc}</p>
      {outcome && (
        <div className="font-hand text-marigold text-base leading-snug">
          {outcome}
        </div>
      )}
      {children}
      <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-royal">
        Open <ArrowRight className="w-4 h-4" />
      </div>
    </Link>
  );
}

function AudienceTile({ label, sub }) {
  return (
    <div className="rounded-xl p-3 bg-cream border-2 border-royal shadow-[3px_3px_0_0_#2E1A47] hover-lift">
      <div className="inline-flex items-center gap-1.5 mb-1"><Users className="w-3 h-3 text-marigold" /> <span className="text-[10px] uppercase tracking-widest font-bold text-ink2">{sub}</span></div>
      <div className="font-heading font-semibold text-sm text-royal">{label}</div>
    </div>
  );
}

function Testimonial({ body, name, role, project, videoUrl }) {
  return (
    <blockquote className="rounded-2xl border-2 border-royal bg-cream p-5 shadow-[4px_4px_0_0_#2E1A47] hover-lift">
      {videoUrl ? (
        <video
          src={`${process.env.REACT_APP_BACKEND_URL}${videoUrl}`}
          className="w-full aspect-video rounded-xl border-2 border-royal object-cover bg-royal mb-3"
          controls
          playsInline
          preload="metadata"
          data-testid="testimonial-video"
        />
      ) : (
        <Star className="w-4 h-4 text-gold fill-gold mb-3" />
      )}
      <p className="text-ink leading-relaxed text-sm mb-4">&ldquo;{body}&rdquo;</p>
      <div className="font-hand text-xl text-marigold leading-none">{name}</div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-ink2 mt-0.5">{role}</div>
      {project && <div className="text-[10px] text-teal font-mono mt-1">→ {project}</div>}
    </blockquote>
  );
}

function TestimonialWall() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/testimonials?limit=9").then(({ data }) => setRows(data.testimonials || [])).catch(() => setRows([]));
  }, []);
  if (rows === null) {
    return <div className="text-center py-8 text-ink2 text-sm"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>;
  }
  if (rows.length === 0) {
    return (
      <div className="text-center py-8 text-ink2 text-sm" data-testid="testimonial-wall-empty">
        Nothing shipped yet — you could be first. Tap <em>Share your story</em>.
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="testimonial-wall">
      {rows.map((t) => (
        <Testimonial
          key={t.id}
          body={t.body}
          name={t.author_name}
          role={t.role}
          project={t.project_name}
          videoUrl={t.video_url}
        />
      ))}
    </div>
  );
}

function TestimonialSubmitButton() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [form, setForm] = useState({ body: "", role: "", project_name: "" });
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [recorder, setRecorder] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const MAX_SECS = 30;

  const resetVideo = () => {
    if (videoBlob) URL.revokeObjectURL(videoBlob);
    setVideoBlob(null); setVideoUrl(null); setRecordSecs(0);
  };

  const uploadBlob = async (blob) => {
    const token = localStorage.getItem("dpa_token");
    const fd = new FormData();
    const ext = (blob.type || "video/webm").split("/")[1].split(";")[0];
    fd.append("file", blob, `selfie.${ext}`);
    setBusy(true);
    try {
      const r = await fetch(`${API}/testimonials/upload-video`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      const j = await r.json();
      setVideoUrl(j.video_url);
      toast.success("Video uploaded — ready to send.");
    } catch (e) {
      toast.error(e.message || "Video upload failed");
    } finally { setBusy(false); }
  };

  const startRecording = async () => {
    if (!user) { toast.info("Sign in first to record a story."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640 }, audio: true });
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
          ? "video/webm;codecs=vp8,opus"
          : "video/webm";
      const rec = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        if (videoBlob) URL.revokeObjectURL(videoBlob);
        setVideoBlob(url);
        setRecording(false);
        await uploadBlob(blob);
      };
      rec.start();
      setRecorder(rec); setRecording(true); setRecordSecs(0);
    } catch (e) {
      toast.error("Camera/mic access denied. Try uploading a file instead.");
    }
  };

  const stopRecording = () => {
    if (recorder && recorder.state !== "inactive") recorder.stop();
  };

  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 30 * 1024 * 1024) { toast.error("Video must be under 30MB."); return; }
    const url = URL.createObjectURL(f);
    if (videoBlob) URL.revokeObjectURL(videoBlob);
    setVideoBlob(url);
    await uploadBlob(f);
  };

  useEffect(() => {
    if (!recording) return;
    const id = setInterval(() => {
      setRecordSecs((s) => {
        const next = s + 1;
        if (next >= MAX_SECS) { stopRecording(); }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Sign in first to share your story."); return; }
    if (form.body.trim().length < 20) { toast.error("Please share a little more (20+ chars)."); return; }
    setBusy(true);
    try {
      await api.post("/testimonials", { ...form, video_url: videoUrl || null });
      setOk(true);
      setForm({ body: "", role: "", project_name: "" });
      resetVideo();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please try again.");
    } finally { setBusy(false); }
  };

  const closeModal = () => {
    if (recording) stopRecording();
    resetVideo();
    setOpen(false);
  };

  return (
    <>
      <button onClick={() => { setOpen(true); setOk(false); }} className="btn-outline-ink text-sm hover-lift" data-testid="testimonial-submit-open">
        <MessageSquarePlus className="w-4 h-4" /> {t("testimonials.submit", "Share your story")}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-royal/60 flex items-center justify-center p-4" onClick={() => !busy && closeModal()}>
          <div className="bg-cream border-2 border-royal rounded-3xl shadow-[10px_10px_0_0_#2E1A47] w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="testimonial-submit-modal">
            <div className="p-4 border-b-2 border-royal flex items-center justify-between bg-gold/20">
              <div>
                <div className="royal-eyebrow mb-1">{t("testimonials.submit", "Share your story")}</div>
                <div className="font-heading text-lg font-semibold text-royal">{t("testimonials.submit_body", "Tell us how your build went.")}</div>
              </div>
              <button onClick={closeModal} className="text-ink2 text-xl w-8 h-8">×</button>
            </div>
            {ok ? (
              <div className="p-8 text-center space-y-3" data-testid="testimonial-submit-ok">
                <Star className="w-8 h-8 mx-auto text-gold fill-gold" />
                <p className="text-ink">{t("testimonials.pending", "Thank you — we'll review and post soon.")}</p>
                <button onClick={closeModal} className="btn-royal text-sm">Close</button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-4 space-y-3" data-testid="testimonial-submit-form">
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder={t("testimonials.submit_placeholder", "What did you build?")}
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2 rounded-xl border-2 border-royal bg-cream text-sm resize-none"
                  data-testid="testimonial-body-input"
                />
                <div className="text-right text-[10px] text-ink2">{form.body.length}/500</div>
                <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder={t("testimonials.submit_role", "Your role (e.g., student, teacher)")} className="w-full px-3 py-2 rounded-xl border-2 border-royal bg-cream text-sm" data-testid="testimonial-role-input" />
                <input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} placeholder={t("testimonials.submit_project", "Project name (optional)")} className="w-full px-3 py-2 rounded-xl border-2 border-royal bg-cream text-sm" data-testid="testimonial-project-input" />

                <div className="rounded-xl border-2 border-dashed border-royal/40 p-3 space-y-2" data-testid="testimonial-video-recorder">
                  {videoBlob ? (
                    <div className="space-y-2">
                      <video src={videoBlob} controls className="w-full aspect-video rounded-lg border-2 border-royal bg-royal" data-testid="testimonial-video-preview" />
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-widest text-teal font-bold">
                          {videoUrl ? t("testimonials.submit_video_ready", "Video ready") : "Uploading…"}
                        </span>
                        <button type="button" onClick={resetVideo} className="text-xs text-marigold underline" data-testid="testimonial-video-clear">
                          <X className="w-3 h-3 inline" /> {t("testimonials.submit_video_clear", "Remove")}
                        </button>
                      </div>
                    </div>
                  ) : recording ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-sm text-marigold font-semibold">
                        <Circle className="w-3 h-3 fill-red-500 text-red-500 animate-pulse" />
                        Recording · {recordSecs}s / {MAX_SECS}s
                      </div>
                      <button type="button" onClick={stopRecording} className="btn-outline-ink text-xs" data-testid="testimonial-video-stop">
                        {t("testimonials.submit_video_stop", "Stop")}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <button type="button" onClick={startRecording} disabled={busy} className="btn-outline-ink text-xs" data-testid="testimonial-video-record">
                        <Video className="w-3.5 h-3.5" /> {t("testimonials.submit_video", "Record")}
                      </button>
                      <label className="text-xs text-ink2 cursor-pointer underline">
                        <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={onFilePicked} className="hidden" data-testid="testimonial-video-file" />
                        {t("testimonials.submit_video_pick", "or upload a file")}
                      </label>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={busy || recording} className="btn-royal w-full" data-testid="testimonial-submit-send">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>{t("testimonials.submit_send", "Send story")}</>}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function TierCard({ name, price, tag, desc, highlight, testid }) {
  return (
    <div className={`rounded-2xl border-2 border-royal p-4 relative hover-lift ${highlight ? "bg-gold/20 shadow-[6px_6px_0_0_#2E1A47]" : "bg-cream shadow-[3px_3px_0_0_#2E1A47]"}`} data-testid={testid}>
      {highlight && (
        <span className="absolute -top-2.5 left-3 text-[9px] uppercase tracking-widest font-bold text-parchment bg-royal border-2 border-royal rounded-full px-2 py-0.5">Popular</span>
      )}
      <div className="font-heading font-semibold text-lg text-royal">{name}</div>
      <div className="font-heading text-3xl font-semibold my-1 text-royal">{price}</div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-marigold mb-2">{tag}</div>
      <div className="text-xs text-ink2 leading-relaxed">{desc}</div>
      <div className="mt-3 flex items-center gap-1 text-[10px] text-teal font-bold uppercase tracking-widest">
        <Check className="w-3 h-3" /> All 6 apps
      </div>
    </div>
  );
}
